import {
  Breadcrumb,
  Button,
  createSlotRecipeContext,
  Heading,
  Text,
  type HTMLChakraProps,
} from '@chakra-ui/react'
import { Fragment, type ReactNode } from 'react'
import { LuChevronLeft } from 'react-icons/lu'
import { Link } from 'react-router-dom'

import { pageRecipe } from '@/theme/recipes/page.recipe'

const { withProvider, withContext } = createSlotRecipeContext({ recipe: pageRecipe })

export const PageRoot = withProvider<HTMLDivElement, HTMLChakraProps<'div'>>('div', 'root')
export const PageContent = withContext<HTMLDivElement, HTMLChakraProps<'div'>>('div', 'content')
export const PageSection = withContext<HTMLElement, HTMLChakraProps<'section'>>(
  'section',
  'section'
)
export const PageOverviewGrid = withContext<HTMLDivElement, HTMLChakraProps<'div'>>(
  'div',
  'overviewGrid'
)
export const PageNavigationColumns = withContext<HTMLDivElement, HTMLChakraProps<'div'>>(
  'div',
  'navigationColumns'
)
export const PageContextColumns = withContext<HTMLDivElement, HTMLChakraProps<'div'>>(
  'div',
  'contextColumns'
)
export const PageNavigation = withContext<HTMLElement, HTMLChakraProps<'nav'>>('nav', 'navigation')
export const PageContext = withContext<HTMLElement, HTMLChakraProps<'aside'>>('aside', 'context')
export const PageMain = withContext<HTMLDivElement, HTMLChakraProps<'div'>>('div', 'main')

type BreadcrumbItem = {
  label: string
  to?: string
}

type PageHeaderProps = {
  breadcrumbs: BreadcrumbItem[]
  backLabel?: string
  onBack: () => void
  title: ReactNode
  meta?: ReactNode
  titleAddon?: ReactNode
}

const HeaderSlot = withContext<HTMLElement, HTMLChakraProps<'header'>>('header', 'header')
const BreadcrumbSlot = withContext<HTMLDivElement, HTMLChakraProps<'div'>>('div', 'breadcrumb')
const HeadingRowSlot = withContext<HTMLDivElement, HTMLChakraProps<'div'>>('div', 'headingRow')
const TitleGroupSlot = withContext<HTMLDivElement, HTMLChakraProps<'div'>>('div', 'titleGroup')

export function PageHeader({
  breadcrumbs,
  backLabel = 'Zurück',
  onBack,
  title,
  meta,
  titleAddon,
}: PageHeaderProps) {
  return (
    <HeaderSlot>
      <BreadcrumbSlot>
        <Breadcrumb.Root>
          <Breadcrumb.List>
            {breadcrumbs.map((item, index) => {
              const current = index === breadcrumbs.length - 1
              return (
                <Fragment key={`${item.label}-${index}`}>
                  <Breadcrumb.Item>
                    {current || !item.to ? (
                      <Breadcrumb.CurrentLink>{item.label}</Breadcrumb.CurrentLink>
                    ) : (
                      <Breadcrumb.Link asChild>
                        <Link to={item.to}>{item.label}</Link>
                      </Breadcrumb.Link>
                    )}
                  </Breadcrumb.Item>
                  {/* The separator renders its own <li>, so it is a sibling of
                      the item rather than a child of it. */}
                  {!current && <Breadcrumb.Separator />}
                </Fragment>
              )
            })}
          </Breadcrumb.List>
        </Breadcrumb.Root>
      </BreadcrumbSlot>

      <Button variant="subtle" colorPalette="gray" onClick={onBack}>
        <LuChevronLeft />
        {backLabel}
      </Button>

      <TitleGroupSlot>
        <HeadingRowSlot>
          <Heading textStyle="pageTitle">{title}</Heading>
          {titleAddon}
        </HeadingRowSlot>
        {meta && <Text color="fg.muted">{meta}</Text>}
      </TitleGroupSlot>
    </HeaderSlot>
  )
}

export const Page = {
  Root: PageRoot,
  Header: PageHeader,
  Content: PageContent,
  Section: PageSection,
  OverviewGrid: PageOverviewGrid,
  NavigationColumns: PageNavigationColumns,
  ContextColumns: PageContextColumns,
  Navigation: PageNavigation,
  Context: PageContext,
  Main: PageMain,
}
